import * as _ from 'lodash-es';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type {
  ExpandableAreas,
  PriorityList,
} from './usePostLayoutStateReducer';
import usePostLayoutStateReducer from './usePostLayoutStateReducer';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import { LAYOUT_DEBOUNCE_TIMEOUT } from '../app/layout/SidebarShiftStateProvider';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { UIActions } from '../ui/uiSlice';
import PostLayoutSizeProvider from '../utils/responsive/PostLayoutSizeProvider';
import type { PostLayoutSize } from '../utils/responsive/Sizes';
import { getCurrentPostLayoutSize } from '../utils/responsive/Sizes';

type PostLayoutState = {
  isTOCExpanded: boolean;
  canTOCExpand: boolean;
  canMagneticWallExpand: boolean;
  isMagneticWallExpanded: boolean;
};

const PostLayoutStateContext = React.createContext<PostLayoutState>({
  isTOCExpanded: false,
  canTOCExpand: false,
  canMagneticWallExpand: false,
  isMagneticWallExpanded: false,
});

const ToggleTOCContext = React.createContext<(forceValue?: boolean) => void>(
  _.noop,
);

const useObservedPostLayoutSize = () => {
  const [postLayoutSize, setPostLayoutSize] = useState<PostLayoutSize>(() =>
    getCurrentPostLayoutSize({
      baseWidth: window.innerWidth,
    }),
  );

  useEffect(() => {
    const handleResize = () => {
      const newPostLayoutSize = getCurrentPostLayoutSize({
        baseWidth: window.innerWidth,
      });
      setPostLayoutSize(newPostLayoutSize);
    };

    handleResize();

    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );
    window.addEventListener('resize', debouncedHandleResize, { passive: true });
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, []);

  return postLayoutSize;
};

interface PostLayoutProviderProps {
  children: React.ReactNode;
  expandableAreas: ExpandableAreas;
  initialPriorityList?: PriorityList;
}

const PostLayoutProvider = ({
  children,
  expandableAreas,
  initialPriorityList,
}: PostLayoutProviderProps) => {
  const postLayoutSize = useObservedPostLayoutSize();

  const isSidebarOpen = useIsSidebarOpen();
  const dispatch = useAppDispatch();

  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const {
    state: { expandedAreas },
    dispatch: dispatchPostLayoutAction,
    canMagneticWallExpand,
    canTOCExpand,
  } = usePostLayoutStateReducer({
    isSidebarOpenInitially: isSidebarOpen,
    expandableAreas,
    initialPriorityList,
  });

  const isMagneticWallExpanded = expandedAreas.has('magneticWall');
  const isTOCExpanded = expandedAreas.has('toc');
  const shouldOpenSidebar = expandedAreas.has('sidebar');

  // Sync back sidebar state
  useEffect(() => {
    dispatch(UIActions.toggleSidebar(shouldOpenSidebar));
  }, [dispatch, shouldOpenSidebar]);

  const toggleTOC = useCallback(
    (forceValue?: boolean) => {
      const isTOCExpandedNext = forceValue ?? !isTOCExpanded;

      dispatchPostLayoutAction({
        type: 'TOGGLE_AREA',
        payload: {
          area: 'toc',
          isExpanded: isTOCExpandedNext,
        },
      });
    },
    [isTOCExpanded, dispatchPostLayoutAction],
  );

  useEffect(() => {
    if (magneticWallFocus?.hasExpandIntent) {
      dispatchPostLayoutAction({
        type: 'TOGGLE_AREA',
        payload: {
          area: 'magneticWall',
          isExpanded: true,
        },
      });
    }
  }, [dispatchPostLayoutAction, magneticWallFocus?.hasExpandIntent]);

  useLayoutEffect(() => {
    dispatchPostLayoutAction({
      type: 'TOGGLE_AREA',
      payload: {
        area: 'sidebar',
        isExpanded: isSidebarOpen,
      },
    });
  }, [dispatchPostLayoutAction, isSidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      dispatchPostLayoutAction({ type: 'RESIZE', payload: window.innerWidth });
    };

    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );

    window.addEventListener('resize', debouncedHandleResize, { passive: true });
    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [dispatchPostLayoutAction]);

  const postLayoutState = useMemo<PostLayoutState>(() => {
    return {
      isTOCExpanded,
      canTOCExpand,
      canMagneticWallExpand,
      isMagneticWallExpanded,
    };
  }, [
    canTOCExpand,
    isTOCExpanded,
    canMagneticWallExpand,
    isMagneticWallExpanded,
  ]);

  return (
    <ToggleTOCContext.Provider value={toggleTOC}>
      <PostLayoutStateContext.Provider value={postLayoutState}>
        <PostLayoutSizeProvider postLayoutSize={postLayoutSize}>
          {children}
        </PostLayoutSizeProvider>
      </PostLayoutStateContext.Provider>
    </ToggleTOCContext.Provider>
  );
};

export const usePostLayoutState = () => useContext(PostLayoutStateContext);
export const useToggleTOC = () => useContext(ToggleTOCContext);

export default PostLayoutProvider;
